import { Box, LinearProgress, Stack, styled, Tooltip, Typography } from '@mui/material';
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridActionsCellItem,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { isEmpty } from 'lodash';
import { SyntheticEvent } from 'react';
import TrainingIcon from '../../resources/images/icons-certificate.svg';
import MoreVertIcon from '../../resources/images/icons-more-vertical.svg';
import { KLAYO_COLORS } from '../../themes';
import type { BaseTableRow, TableColumnDef, TableProps } from '../../types/components/table.types';
import { highlightKeyword } from '../../utilities';
import { Chip } from '../Chip';
import { Table } from './Table';

/** Row data for StandardLibraryTable */
interface StandardLibraryTableRow extends BaseTableRow {
  id?: string;
  jobDefinitionId?: string;
  name?: string;
  description?: string;
  category?: string;
  numberOfCompetencies?: number;
  numberOfTasks?: number;
  numberOfJobDefinitions?: number;
  numberOfJobAttributes?: number;
  numberOfJobs?: number;
  competencyCategories?: Array<{ name: string }>;
  jobDefinitionDomain?: { name?: string };
  hasExpiryDate?: boolean;
  trainings?: Array<{ courseName: string }>;
  hasTraining?: boolean;
  hierarchy?: string[];
  score?: number;
  [key: string]: unknown;
}

/** Props for StandardLibraryTable */
interface StandardLibraryTableProps<
  TData extends StandardLibraryTableRow = StandardLibraryTableRow,
> extends TableProps<TData> {
  onJobSelect?: (event: SyntheticEvent, row: TData) => void;
  onActionMenu?: (event: React.MouseEvent<HTMLButtonElement>, row: TData) => void;
  queryText?: string;
}

/** State for StandardLibraryTable */
interface StandardLibraryTableState {
  rowHasAction: ((row: StandardLibraryTableRow) => boolean) | null;
}

export class StandardLibraryTable<
  TData extends StandardLibraryTableRow = StandardLibraryTableRow,
> extends Table<TData, StandardLibraryTableProps<TData>> {
  static columns = {
    name: {
      field: 'name',
      label: 'Job',
    },
    nameSuggestion: {
      field: 'name',
      label: 'Job',
    },
    jobCategory: {
      field: 'category',
      label: 'Category',
    },
    jobDescription: {
      field: 'description',
      label: 'Description',
    },
    competencies: {
      field: 'numberOfCompetencies',
      label: 'Competencies',
    },
    competenciesName: {
      field: 'numberOfCompetencies',
      label: 'Competencies',
    },
    tasks: {
      field: 'numberOfTasks',
      label: 'Tasks',
    },
    jobs: {
      field: 'numberOfJobDefinitions',
      label: 'Jobs',
    },
    listCount: {
      field: 'listCount',
      label: '',
    },
    generalCount: {
      field: 'generalCount',
      label: '',
    },
    category: {
      field: 'attributeCategories',
      label: 'Category',
    },
    expiry: {
      field: 'expiry',
      label: 'expiry',
    },
    training: {
      field: 'hasTraining',
      label: 'Training',
    },
    domain: {
      field: 'domain',
      label: 'Domain',
    },
    actions: {
      field: 'actions',
      label: 'Actions',
    },
    traningsIcon: {
      field: 'traningsIcon',
      label: 'Training',
    },
    checkbox: {
      field: 'checkbox',
    },
    matchPercentages: {
      field: 'matchPercentages',
      label: 'Match Percentages',
    },
    jobSummary: {
      field: 'jobSummary',
      label: 'Job Summary',
    },
  };

  static defaultProps = {
    ...Table.defaultProps,
  };

  constructor(props: StandardLibraryTableProps<TData>) {
    super(props);

    (this.state as unknown as StandardLibraryTableState) = {
      ...this.state,
      rowHasAction: null,
    };
  }

  override getRowId(e: TData): string {
    return String(e.id ?? e.jobDefinitionId ?? '');
  }

  getTreeDataPath(row: TData): string[] {
    return row.hierarchy ?? [];
  }

  onRowClick(params: GridRowParams<TData>, e: SyntheticEvent): void {
    const { onJobSelect } = this.props;
    if (onJobSelect) onJobSelect(e, params.row);
  }

  getProgressColor(score: number) {
    KLAYO_COLORS;
    if (score >= 70) return KLAYO_COLORS.LimeGreenLG600;
    if (score <= 30) return KLAYO_COLORS.GoldGD700;
    return KLAYO_COLORS.NeonBlueNB300;
  }

  initColumns(): void {
    const { columns, onActionMenu = () => {}, queryText = '', keyword } = this.props;

    this.columns = [];

    columns?.forEach((c) => {
      if (this.hasColumn(c)) {
        if (c.type === StandardLibraryTable.columns.name)
          this.columns.push({
            field: c.field || StandardLibraryTable.columns.name.field,
            headerName: c.label || c.type.label || '',
            sortable: c.sortable ?? true,
            filterable: false,
            disableReorder: true,
            flex: 1,
            valueGetter: (_value, row) => row.name,
            renderCell: (params) => (
              <Tooltip title={params.row.name ?? ''}>
                <div className='truncate'>{highlightKeyword(params.row.name ?? '', keyword)}</div>
              </Tooltip>
            ),
          });
        if (c.type === StandardLibraryTable.columns.nameSuggestion)
          this.columns.push({
            field: c.field || StandardLibraryTable.columns.nameSuggestion.field,
            headerName: c.label || c.type?.label || '',
            sortable: true,
            filterable: false,
            disableReorder: true,
            flex: 1,
            valueGetter: (_value, row) => row.name,
            renderCell: (params) => {
              const text = params.row.name ?? '';
              const matches = match(text, queryText, {
                insideWords: true,
                requireMatchAll: false,
              });
              const parts = parse(text, matches);
              return (
                <StyledHighlight>
                  {parts.map((part, index) => (
                    <span
                      key={index}
                      className={
                        'klayo-selector_itempart' +
                        (part.highlight === true ? ' klayo-selector_highlight' : '')
                      }
                    >
                      {part.text}
                    </span>
                  ))}
                </StyledHighlight>
              );
            },
          });
        if (c.type === StandardLibraryTable.columns.competencies)
          this.columns.push({
            field: c.field || StandardLibraryTable.columns.competencies.field,
            headerName: c.label || c.type.label || '',
            sortable: true,
            filterable: false,
            disableReorder: true,
            width: 150,
            type: 'number',
            valueGetter: (_value, row) => row.numberOfCompetencies,
            ...(c.enableChip && {
              renderCell: (params) => (
                <Chip
                  label={params.row.numberOfCompetencies}
                  onClick={(e) => (c?.onClick ? c?.onClick(e, params.row) : null)}
                  textColor={KLAYO_COLORS.NeonBlueNB800}
                  backgroundColor={KLAYO_COLORS.NeonBlueNB050}
                  sx={{ '& .MuiChip-root .MuiChip-label': { paddingBottom: 0 } }}
                />
              ),
            }),
          });

        if (c.type === StandardLibraryTable.columns.competenciesName)
          this.columns.push({
            field: c.field || StandardLibraryTable.columns.competenciesName.field,
            headerName: c.label || c.type.label || '',
            sortable: true,
            filterable: false,
            disableReorder: true,
            width: 150,
            type: 'number',
            valueGetter: (_value, row) => row.numberOfCompetencies,
            renderCell: (params) => {
              const count = params.row.numberOfCompetencies ?? 0;
              const translationKey =
                count > 1
                  ? 'tableColumns.competenciesCountPlural'
                  : 'tableColumns.competenciesCount';
              const displayText = this.props.translate
                ? this.props.translate(translationKey, { count })
                : `${count} ${count > 1 ? 'competencies' : 'competency'}`;
              return (
                <Typography sx={{ fontSize: '14px', color: KLAYO_COLORS.GreyGR500 }}>
                  {displayText}
                </Typography>
              );
            },
          });
        if (c.type === StandardLibraryTable.columns.jobDescription)
          this.columns.push({
            field: c.field || StandardLibraryTable.columns.jobDescription.field,
            headerName: c.label || c.type.label || '',
            sortable: true,
            filterable: false,
            disableReorder: true,
            flex: 1,
            width: 100,
            valueGetter: (_value, row) => row.description,
            renderCell: (params) => {
              return (
                <Tooltip title={params.row.description} arrow>
                  <div
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {params.row.description}
                  </div>
                </Tooltip>
              );
            },
          });
        if (c.type === StandardLibraryTable.columns.tasks)
          this.columns.push({
            field: c.field || StandardLibraryTable.columns.tasks.field,
            headerName: c.label || c.type.label || '',
            sortable: true,
            filterable: false,
            disableReorder: true,
            width: 100,
            type: 'number',
            ...(c.enableChip && {
              renderCell: (params) => (
                <Chip
                  label={params.row.numberOfTasks}
                  sx={{ '& .MuiChip-root .MuiChip-label': { paddingBottom: 0 } }}
                  onClick={(e) => (c?.onClick ? c?.onClick(e, params.row) : null)}
                  textColor={KLAYO_COLORS.NeonBlueNB800}
                  backgroundColor={KLAYO_COLORS.NeonBlueNB050}
                />
              ),
            }),
            valueGetter: (_value, row) => row.numberOfTasks,
          });
        if (c.type === StandardLibraryTable.columns.jobs)
          this.columns.push({
            field: c.field || StandardLibraryTable.columns.jobs.field,
            headerName: c.label || c.type.label || '',
            sortable: true,
            filterable: false,
            disableReorder: true,
            width: 80,
            type: 'number',
            valueGetter: (_value, row) =>
              row.numberOfJobAttributes ?? row.numberOfJobDefinitions ?? row.numberOfJobs,
            ...(c.enableChip && {
              renderCell: (params) => (
                <Chip
                  onClick={(e) => (c?.onClick ? c?.onClick(e, params.row) : null)}
                  textColor={KLAYO_COLORS.NeonBlueNB800}
                  backgroundColor={KLAYO_COLORS.NeonBlueNB050}
                  sx={{ '& .MuiChip-root .MuiChip-label': { paddingBottom: 0 } }}
                  label={
                    params.row.numberOfJobAttributes ??
                    params.row.numberOfJobDefinitions ??
                    params.row.numberOfJobs
                  }
                />
              ),
            }),
          });
        if (c.type === StandardLibraryTable.columns.category)
          this.columns.push({
            field: c.field || StandardLibraryTable.columns.category.field,
            headerName: c.label || c.type.label || '',
            sortable: true,
            filterable: false,
            disableReorder: true,
            width: 140,
            align: c.align,
            renderCell: (params) => {
              const categories = params.row?.competencyCategories || [];
              if (categories.length === 0) {
                return this.props.translate?.('common.na') ?? 'n/a';
              }

              const categoryNames = categories.map((cat: { name: string }) => cat.name);
              const displayText = categoryNames.join(', ');

              return (
                <Tooltip data-style-id='flex-end' title={displayText} arrow>
                  <Typography
                    sx={{ color: c.blur ? KLAYO_COLORS.GreyGR500 : 'inherit', fontSize: '14px' }}
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {displayText}
                  </Typography>
                </Tooltip>
              );
            },
            valueGetter: (_value, row) => {
              const categories = row?.competencyCategories || [];
              if (categories.length === 0) return this.props.translate?.('common.na') ?? 'n/a';
              return categories.map((cat: { name: string }) => cat.name).join(', ');
            },
          });
        if (c.type === StandardLibraryTable.columns.domain)
          this.columns.push({
            field: c.field || StandardLibraryTable.columns.domain.field,
            headerName: c.label || c.type.label || '',
            sortable: true,
            filterable: false,
            width: 125,
            className: 'domain-column',
            valueGetter: (_value, row) => row?.jobDefinitionDomain?.name,
            renderCell: (params) => {
              return (
                <Tooltip title={params.row?.jobDefinitionDomain?.name || ''} arrow>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: KLAYO_COLORS.GreyGR500,
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {params.row?.jobDefinitionDomain?.name}
                  </Typography>
                </Tooltip>
              );
            },
          });
        if (c.type === StandardLibraryTable.columns.expiry)
          this.columns.push({
            field: c.field || StandardLibraryTable.columns.expiry.field,
            headerName: c.label || c.type.label || '',
            sortable: true,
            filterable: false,
            disableReorder: true,
            width: 100,
            align: 'center',
            headerAlign: 'center',
            valueGetter: (_value, row) =>
              row.hasExpiryDate
                ? (this.props.translate?.('common.yes') ?? 'Yes')
                : (this.props.translate?.('common.no') ?? 'No'),
            renderCell: (params) => {
              return (
                <Typography sx={{ fontSize: '14px' }}>
                  {params.row.hasExpiryDate
                    ? (this.props.translate?.('common.yes') ?? 'Yes')
                    : (this.props.translate?.('common.no') ?? 'No')}
                </Typography>
              );
            },
          });
        if (c.type === StandardLibraryTable.columns.training) {
          this.columns.push({
            field: c.field || StandardLibraryTable.columns.training.field,
            headerName:
              c.label || this.props.translate?.('tableColumns.training') || c.type.label || '',
            sortable: true,
            filterable: false,
            disableReorder: true,
            width: 100,
            align: 'center',
            headerAlign: 'center',
            valueGetter: (_value, row) =>
              !isEmpty(row.trainings)
                ? (this.props.translate?.('common.yes') ?? 'Yes')
                : (this.props.translate?.('common.no') ?? 'No'),
            renderCell: (params) => {
              return (
                <Typography sx={{ fontSize: '14px' }}>
                  {!isEmpty(params.row.trainings)
                    ? (this.props.translate?.('common.yes') ?? 'Yes')
                    : (this.props.translate?.('common.no') ?? 'No')}
                </Typography>
              );
            },
          });
        }

        if (c.type === StandardLibraryTable.columns.jobCategory)
          this.columns.push({
            field: c.field || StandardLibraryTable.columns.jobCategory.field,
            sortable: true,
            filterable: false,
            disableReorder: true,
            width: 50,
            valueGetter: (_value, row) => row.category,
          });
        if (c.type === StandardLibraryTable.columns.traningsIcon)
          this.columns.push({
            field: 'traningsIcon',
            type: 'actions',
            filterable: false,
            align: 'right',
            disableReorder: true,
            width: 50,
            renderCell: (params) => {
              const training = params.row?.trainings || [];

              return (
                <Tooltip title={training.map((t) => t.courseName).join(', ')} arrow placement='top'>
                  <StyledTrainingIcon
                    sx={{ display: training.length > 0 ? 'flex' : 'none', width: '90%' }}
                  >
                    <TrainingIcon />
                  </StyledTrainingIcon>
                </Tooltip>
              );
            },
          });
        if (c.type === StandardLibraryTable.columns.actions)
          this.columns.push({
            field: 'actions',
            type: 'actions',
            filterable: false,
            align: 'right',
            width: '50',
            disableReorder: true,
            getActions: (params) => [
              <GridActionsCellItem
                icon={<MoreVertIcon />}
                label='Actions'
                onClick={(e) => onActionMenu(e, params.row)}
                key={Math.random().toString(36).substring(7)}
              />,
            ],
          });
        if (c.type === StandardLibraryTable.columns.listCount)
          this.columns.push({
            field: c.field || 'listCount',
            headerName: c.label || c.type.label || '',
            sortable: true,
            filterable: false,
            disableReorder: true,
            width: c.width || 100,
            type: 'number',
            valueGetter: (_value, row) => {
              const fieldValue = c.field ? row[c.field as keyof TData] : undefined;
              return Array.isArray(fieldValue) ? fieldValue.length : 0;
            },
          });
        if (c.type === StandardLibraryTable.columns.generalCount)
          this.columns.push({
            field: c.field || 'generalCount',
            headerName: c.label || c.type.label || '',
            sortable: true,
            filterable: false,
            disableReorder: true,
            width: c.width || 100,
            type: 'number',
            valueGetter: (_value, row) => {
              const fieldValue = c.field ? row[c.field as keyof TData] : 0;
              return typeof fieldValue === 'number' ? fieldValue : 0;
            },
          });
        if (c.type === StandardLibraryTable.columns.checkbox)
          this.columns.push(GRID_CHECKBOX_SELECTION_COL_DEF as TableColumnDef<TData>);
        if (c.type === StandardLibraryTable.columns.matchPercentages)
          this.columns.push({
            field: c.field || StandardLibraryTable.columns.matchPercentages.field,
            headerName: c.label || c.type.label || '',
            sortable: false,
            filterable: false,
            disableReorder: true,
            flex: 1,
            valueGetter: (_value, row) => row.score,
            renderCell: (params) => {
              const score = Number((params.row?.score ?? 0).toFixed(2));

              return (
                <Box display='flex' alignItems='center' gap={1} flex={1} width='100%'>
                  <Tooltip title={`${score}%`} arrow>
                    <LinearProgress
                      variant='determinate'
                      value={score}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: '#E5E7EB',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: this.getProgressColor(score),
                          borderRadius: 4,
                        },
                        flex: 1,
                      }}
                    />
                  </Tooltip>
                  <Typography
                    sx={{
                      minWidth: 36,
                    }}
                    variant='body2'
                    color='textSecondary'
                  >
                    {score}%
                  </Typography>
                </Box>
              );
            },
          });

        if (c.type === StandardLibraryTable.columns.jobSummary)
          this.columns.push({
            field: c.field || StandardLibraryTable.columns.jobSummary.field,
            headerName: c.label || c.type.label || '',
            sortable: true,
            filterable: false,
            disableReorder: true,
            flex: 1,
            valueGetter: (_value, row) => row.name,
            renderCell: (params) => {
              return (
                <Stack gap={0.5}>
                  <Typography variant='body1' fontWeight={500} color={KLAYO_COLORS.GreyGR900}>
                    {params.row.name}
                  </Typography>
                  <Typography variant='body2' color={KLAYO_COLORS.GreyGR500}>
                    {`${this.props?.translate?.('jobBuilder.tasks', { count: params.row.numberOfTasks })} • ${this.props?.translate?.('jobBuilder.competencies', { count: params.row.numberOfCompetencies })} • ${this.props?.translate?.('jobBuilder.trainingCourses', { count: params.row.numberOfTrainings })}`}
                  </Typography>
                </Stack>
              );
            },
          });
      }
    });
  }
}

const StyledTrainingIcon = styled(Box)({
  display: 'flex',
  color: KLAYO_COLORS.NeonBlueNB800,
  scale: 0.8,
  width: '100%',
  "path[fill='#111827']": {
    fill: KLAYO_COLORS.GreyGR400,
  },
});

const StyledHighlight = styled(Typography)({
  fontSize: '14px',
  '& .klayo-selector_highlight': {
    backgroundColor: '#e9e9fe59 !important',
  },
});
